import { useState, useEffect } from 'react';
import apiService from '../services/api';

// Custom hooks for API data
export function useApiHealth() {
  const [isHealthy, setIsHealthy] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        await apiService.request('/health');
        setIsHealthy(true);
        setError(null);
      } catch (err) {
        setIsHealthy(false);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkHealth();
    const interval = setInterval(() => {
      if (!document.hidden) {
        checkHealth();
      }
    }, 120000); // Reduced from 30s to 2 minutes
    return () => clearInterval(interval);
  }, []);

  return { isHealthy, loading, error };
}

export function useDetectionStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await apiService.getStats();
        setStats(data);
        setError(null);
      } catch (err) {
        console.warn('Stats API failed, using mock data:', err.message);
        // Use mock data when API fails
        const { mockStats } = await import('../utils/mockData');
        setStats(mockStats);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchStats();
      }
    }, 120000); // Reduced from 10s to 2 minutes
    return () => clearInterval(interval);
  }, []);

  return { stats, loading, error };
}

export function useAlerts(params = {}) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(0);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const data = await apiService.getAlerts(params);
        setAlerts(data.alerts || data || []);
        setError(null);
        setLastFetch(Date.now());
      } catch (err) {
        console.warn('Alerts API failed, using mock data:', err.message);
        // Use mock data when API fails
        const { mockAlerts } = await import('../utils/mockData');
        setAlerts(mockAlerts);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we haven't fetched in the last 60 seconds
    const now = Date.now();
    if (now - lastFetch > 60000) {
      fetchAlerts();
    }

    // Reduce polling frequency to 120 seconds and only when page is visible
    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchAlerts();
      }
    }, 120000); // Reduced from 5s to 2 minutes
    
    return () => clearInterval(interval);
  }, [JSON.stringify(params), lastFetch]);

  return { alerts, loading, error };
}